import React, { useEffect, useState, ReactElement, useMemo } from "react";
import ReactGA from "react-ga4";
import { useAuth, useUser } from "@/app/providers";
import { Navigate, useLocation } from "react-router";
import { useApolloClient } from "@apollo/client/react";
import { indexedDbDeleteAll, indexedDbDeleteById } from "@/utils";
import { useAppDispatch, logout } from "@/app/store";

interface LocationState {
  loginAs?: boolean;
}

/**
 * Logout component that handles user logout, clears cache and storage,
 * and redirects to appropriate page based on logout context.
 * @returns The logout navigation component
 */
export const Logout = (): ReactElement => {
  const dispatch = useAppDispatch();
  const location = useLocation();
  const state = useMemo(
    () => (location.state as LocationState) || {},
    [location.state],
  );
  const { user, setUser } = useUser();
  const { unsetToken } = useAuth();
  const client = useApolloClient();
  const [logoutCompleted, setLogoutCompleted] = useState<boolean>(false);

  useEffect(() => {
    const handleLogout = async (): Promise<void> => {
      const storageLocale = localStorage.getItem("languageSelect");

      dispatch(logout());

      if (!state || !state?.loginAs) {
        setUser(null);
        unsetToken();
      }

      if (user?.email) {
        ReactGA.event({
          category: "Authentication",
          action: "Logout",
          label: user.email.replace("@", "[at]"),
        });
      }

      localStorage.clear();
      if (storageLocale) {
        localStorage.setItem("languageSelect", storageLocale);
      }

      try {
        await indexedDbDeleteById("cache", "apollo-cache-persist");
      } catch {
        // Silently ignore if specific key does not exist
      }

      try {
        await indexedDbDeleteAll(["cache"]);
      } catch {
        // Silently fail if cache clearing fails
      }

      try {
        await client.clearStore();
      } catch {
        // Silently fail if Apollo store clear fails (e.g. active queries)
        try {
          await client.cache.reset();
        } catch {
          // Silently fail if cache reset fails
        }
      }

      unsetToken();
      setUser(null);
      setLogoutCompleted(true); // Mark the process as complete
    };

    handleLogout().catch(() => {
      setLogoutCompleted(true);
    });
  }, [dispatch, setUser, state, unsetToken, client, user]);

  if (!logoutCompleted) {
    return <div>Loading...</div>; // Show a loading state
  }

  if (state?.loginAs) {
    return <Navigate to="/energy" />;
  } else {
    return <Navigate to="/auth/login" />;
  }
};
